import React, { useState } from "react";
import Layout2 from "../../components/global/layout2";
import GooglePlacesAutocomplete from "../../components/common/GooglePlaceAutoComplete";
import addressModel from "../../models/address.model";
import ApiClient from "../../methods/api/apiClient";
import { toast } from "react-toastify";
import { MdOutlineMessage } from "react-icons/md";
import { FaAcquisitionsIncorporated } from "react-icons/fa";
import { RxCross2 } from "react-icons/rx";
import GooglePlacesAutocompleteWithPin from "../../components/common/GooglePlaceAutoComplete/WithPincode";

export default function RateLookup() {
  // `code` = ZIP if Google Places returned one, otherwise empty.
  // `text` = always-populated fallback ("Chicago, IL") so the backend resolver
  // can still resolve a KMA when no ZIP is available.
  const [origin, setOrigin] = useState({ name: "", code: "", text: "" });
  const [destination, setDestination] = useState({ name: "", code: "", text: "" });
  const [equipment, setEquipment] = useState("Van");
  const [loading, setLoading] = useState(false);
  const [rateData, setRateData] = useState();
  // [{ "lane_id": "", "length_of_haul": 265, "origin": "FSD", "origin_country_code": "USA", "destination": "DSM", "destination_country_code": "USA", "equipment_type": "VAN", "data_timestamp": "2025-09-21", "rpm": 2.87, "rpm_high": 3.07, "rpm_low": 2.69, "rate": 753, "rate_high": 808, "rate_low": 694, "confidence_score": 4, "origin_radius_expansion": 82, "destination_radius_expansion": 82, "timeframe_expansion": 7, "effective_miles": 260, "total_load_count": 14, "total_contributor_count": 5, "input_miles": null }]
  const [error, setError] = useState(null);
  const [show, setShow] = useState(false);
  // Top-level response metadata for the result panel ("just now" / "cached 2h ago" / source).
  const [rateMeta, setRateMeta] = useState({ source: null, cachedAt: null, fetchedAt: null });
  async function fetchRate() {
    setLoading(true);
    setError(null);
    setRateData(null);

    // Either a ZIP OR a "City, ST" text is enough — backend resolves both.
    const originHas = origin.code || origin.text || origin.name;
    const destHas = destination.code || destination.text || destination.name;
    if (!originHas || !destHas) {
      toast.error('Please enter both origin and destination.');
      setLoading(false);
      return;
    }

    const params = {};
    if (origin.code) params.origin_location_postal_code = origin.code;
    else params.origin = origin.text || origin.name;
    if (destination.code) params.destination_location_postal_code = destination.code;
    else params.destination = destination.text || destination.name;

    // Quick Rate Lookup is the only screen allowed to spend a SONAR call.
    // The backend serves cache first and only goes live on a miss when this
    // flag is present; every other caller of /rates stays cache-only.
    params.live = 1;

    ApiClient.get('rates', params).then((res) => {
      if (res?.success) {
        const data = res?.data;
        setRateData(data);
        setRateMeta({
          source: res?.source || null,           // 'cache' | 'fresh' | ...
          cachedAt: res?.cachedAt || null,       // ISO string when cached
          fetchedAt: new Date().toISOString(),   // when this lookup completed
        });
        if ((!data || data.length === 0) && res?.message) {
          setError(res.message);
        }
      } else {
        setError(res?.error || res?.message || 'Something went wrong');
      }
      setLoading(false);
    });
  }

  // Format a "Last updated" line — prefer the cached timestamp (when SONAR
  // was last pulled), fall back to the moment we received this response.
  const formatUpdatedLine = (rate, meta) => {
    const ts = meta?.cachedAt || meta?.fetchedAt;
    if (!ts) return null;
    const d = new Date(ts);
    if (isNaN(d.getTime())) return null;
    const dateStr = d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
    const sourceLabel =
      meta?.source === 'cache' ? 'cached' :
      meta?.source === 'fresh' ? 'live from SONAR' :
      'fetched';
    return `Last updated: ${dateStr} (${sourceLabel})`;
  };

  const addressResult = async (e, type = "origin") => {
    // The autocomplete fires TWO event types:
    //   - "typeChange"  → user is typing; just capture raw text as fallback
    //   - "placeChange" → user picked a suggestion; resolve ZIP + city/state
    const setter = type === "origin" ? setOrigin : setDestination;

    if (e?.event === "typeChange") {
      const typed = e?.value ?? "";
      setter((prev) => ({
        // Keep previously-resolved ZIP only if the typed text still matches the
        // previously selected location's name (otherwise treat as fresh input).
        ...prev,
        name: typed,
        text: typed,
        code: prev?.name === typed ? prev.code : "",
      }));
      return;
    }

    let address = {};
    if (e?.place) {
      address = await addressModel.getAddress(e.place);
    }
    // Build a "City, ST" fallback so the backend can resolve a KMA even
    // when Google Places didn't return a postal code (e.g. picking a city).
    const cityStateText =
      address?.city && address?.stateShortName
        ? `${address.city}, ${address.stateShortName}`
        : (e?.value ?? "");
    setter({ name: e?.value ?? "", code: address?.zipcode ?? "", text: cityStateText });
  };

  return (

    <div>
      <div className="floating-container">
        {/* Hidden checkbox */}
        <input type="checkbox" id="toggle" className="toggle-checkbox" />

        {/* Label acts as floating button */}
        <label htmlFor="toggle" className="floating-btn">
          <span className="plus-icon">  <FaAcquisitionsIncorporated size={22} /></span>
        </label>

        {/* Sliding box */}
        <div className="box">
          <div style={{ padding: "25px" }}>
            <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap mb-4">
              <div className="d-flex align-items-center gap-2 p-0 ">
                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 44 44" fill="none">
                  <path d="M36.5654 10.6865C26.2913 0.375732 8.46347 8.2697 9.28186 22.8555C9.40338 23.5475 9.13765 25.0936 10.2296 25.0246C11.2633 24.7912 10.5789 23.4773 10.6554 22.7829C9.90506 9.4495 26.2067 2.23252 35.5941 11.6596C44.815 20.8977 38.1438 36.7418 25.0923 36.6099C24.0604 36.6155 24.2537 38.1487 25.2596 37.9859C39.4516 37.9748 46.5943 20.7229 36.5654 10.6865Z" fill="#3E549D" />
                  <path d="M12.8179 27.0961C12.8179 27.0058 12.8001 26.9164 12.7656 26.8329C12.731 26.7495 12.6804 26.6737 12.6166 26.6099C12.5527 26.546 12.4769 26.4954 12.3935 26.4608C12.3101 26.4263 12.2206 26.4085 12.1304 26.4086H3.43755C3.25614 26.41 3.08264 26.483 2.95486 26.6118C2.82708 26.7406 2.75537 26.9146 2.75537 27.096C2.75537 27.2775 2.82708 27.4515 2.95486 27.5803C3.08264 27.7091 3.25614 27.7821 3.43755 27.7835H12.1304C12.2207 27.7836 12.3101 27.7658 12.3935 27.7312C12.4769 27.6967 12.5527 27.6461 12.6165 27.5822C12.6804 27.5184 12.731 27.4426 12.7656 27.3592C12.8001 27.2758 12.8179 27.1864 12.8179 27.0961Z" fill="#3E549D" />
                  <path d="M17.9202 32.1984C17.9202 32.1081 17.9024 32.0187 17.8679 31.9352C17.8334 31.8518 17.7827 31.776 17.7189 31.7122C17.655 31.6483 17.5792 31.5977 17.4958 31.5631C17.4124 31.5286 17.323 31.5108 17.2327 31.5109H8.53984C8.35843 31.5123 8.18493 31.5853 8.05715 31.7141C7.92937 31.8429 7.85767 32.0169 7.85767 32.1983C7.85767 32.3798 7.92937 32.5538 8.05715 32.6826C8.18493 32.8114 8.35843 32.8844 8.53984 32.8858H17.2327C17.323 32.8858 17.4124 32.8681 17.4958 32.8335C17.5792 32.799 17.655 32.7484 17.7189 32.6845C17.7827 32.6207 17.8333 32.5449 17.8679 32.4615C17.9024 32.3781 17.9202 32.2887 17.9202 32.1984Z" fill="#3E549D" />
                  <path d="M22.3351 36.6133H13.6422C13.4622 36.6168 13.2907 36.6908 13.1647 36.8193C13.0386 36.9479 12.968 37.1207 12.968 37.3008C12.968 37.4808 13.0386 37.6537 13.1647 37.7822C13.2908 37.9108 13.4622 37.9847 13.6422 37.9882H22.3351C22.5151 37.9847 22.6865 37.9108 22.8126 37.7822C22.9386 37.6537 23.0093 37.4808 23.0093 37.3008C23.0093 37.1207 22.9386 36.9479 22.8126 36.8193C22.6865 36.6908 22.5151 36.6168 22.3351 36.6133Z" fill="#3E549D" />
                  <path d="M25.2542 11.1014C25.1639 11.1014 25.0744 11.1192 24.991 11.1537C24.9076 11.1883 24.8318 11.2389 24.768 11.3027C24.7041 11.3666 24.6535 11.4424 24.6189 11.5258C24.5844 11.6092 24.5666 11.6986 24.5667 11.7889V21.9937C24.5666 22.084 24.5844 22.1734 24.6189 22.2568C24.6535 22.3403 24.7041 22.4161 24.768 22.4799C24.8318 22.5437 24.9076 22.5944 24.991 22.6289C25.0744 22.6635 25.1639 22.6812 25.2542 22.6812H32.8997C32.9904 22.6819 33.0804 22.6646 33.1644 22.6304C33.2484 22.5962 33.3248 22.5457 33.3892 22.4817C33.4537 22.4178 33.5048 22.3418 33.5397 22.2581C33.5745 22.1743 33.5925 22.0845 33.5925 21.9937C33.5925 21.903 33.5745 21.8132 33.5397 21.7294C33.5048 21.6456 33.4537 21.5696 33.3892 21.5057C33.3248 21.4418 33.2484 21.3913 33.1644 21.357C33.0804 21.3228 32.9904 21.3055 32.8997 21.3063H25.9416V11.7889C25.9417 11.6986 25.9239 11.6092 25.8893 11.5258C25.8548 11.4424 25.8042 11.3666 25.7403 11.3027C25.6765 11.2389 25.6007 11.1883 25.5173 11.1537C25.4338 11.1192 25.3444 11.1014 25.2542 11.1014Z" fill="#3E549D" />
                </svg>
                <h2 style={{
                  margin: "0",
                  fontSize: "20px",
                  color: "#000",
                  fontWeight: "600",
                }}>
                  Quick Rate Lookup
                </h2>
              </div>
              <label htmlFor="toggle" className="cursor-pointer ">
                <RxCross2 size={20}/>
              </label>
            </div>
            <div className="modal-body p-0 ">
              {/* ⬇️ Put your form + rate result content here */}
              <form onSubmit={(e) => {
                e.preventDefault();
                fetchRate()
              }}>
                <div
                  className="mb-4"
                  style={{
                    position: "relative",
                    borderRadius: "8px",
                  }}
                >
                  <span
                    className="bg-white px-1 text-xs"
                    style={{ position: "absolute", left: "10px", top: "-10px" }}
                  >
                    Origin (City, ST / ZIP)*
                  </span>
                  <GooglePlacesAutocompleteWithPin
                    key="Map1"
                    value={origin?.name ?? ""}
                    name={"origin"}
                    result={addressResult}
                    required
                    id="address1"
                    placeholder=""
                  />
                </div>

                <div
                  className="mb-4"
                  style={{
                    position: "relative",
                    borderRadius: "8px",
                  }}
                >
                  <span
                    className="bg-white px-1 text-xs"
                    style={{ position: "absolute", left: "10px", top: "-10px" }}
                  >
                    Destination (City, ST / ZIP)*
                  </span>
                  <GooglePlacesAutocompleteWithPin
                    key="Map2"
                    value={destination?.name ?? ""}
                    name={"destinations"}
                    result={(e) => addressResult(e, "destination")}
                    required
                    id="address2"
                    placeholder=""
                  />
                </div>

                <div className="text-center">
                  <button
                    className="btn btn-primary py-1 px-3"
                    type="submit"
                    disabled={loading}
                    style={{ padding: "10px 20px", background: "#3E549D" }}
                  >
                    {loading ? "Loading..." : "Get Rate"}
                  </button>
                </div>
              </form>

              {error && (
                <div style={{ marginTop: 12, color: "red" }}>Error: {error}</div>
              )}

              {rateData?.length > 0 ? (
                <div style={{ marginTop: 20 }}>
                  {rateData.map((rate, index) => (
                    <div
                      key={index}
                      style={{
                        border: "1px solid #ddd",
                        padding: 16,
                        borderRadius: 8,
                        backgroundColor: "#fff",
                        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 22,
                            color: "#2c3e50",
                          }}
                        >
                          $
                          {rate?.rate?.toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          }) ?? "N/A"}
                        </div>
                        <div
                          style={{
                            fontWeight: 500,
                            fontSize: 14,
                            color: "#7f8c8d",
                          }}
                        >
                          ({rate?.rpm?.toFixed(2) ?? "N/A"}/mi)
                        </div>
                      </div>

                      <div
                        style={{
                          fontSize: 14,
                          color: "#34495e",
                          marginTop: 6,
                          fontWeight: 600,
                        }}
                      >
                        {rate?.length_of_haul ?? "N/A"} mi &nbsp;|&nbsp;{" "}
                        {rate?.data_timestamp ?? "N/A"} average
                      </div>

                      <div
                        style={{
                          marginTop: 10,
                          fontSize: 13,
                          color: "#555",
                          lineHeight: 1.4,
                        }}
                      >
                        Range:{" "}
                        <span style={{ fontWeight: "600", color: "#2c3e50" }}>
                          ${rate?.rate_low ?? "N/A"} - ${rate?.rate_high ?? "N/A"}
                        </span>{" "}
                        (
                        <span style={{ fontWeight: "600", color: "#2c3e50" }}>
                          {rate?.rpm_low?.toFixed(2) ?? "N/A"} - {rate?.rpm_high?.toFixed(2) ?? "N/A"}
                        </span>
                        /mi)
                      </div>

                      <div
                        style={{
                          marginTop: 10,
                          fontSize: 13,
                          color: "#2c3e50",
                          fontWeight: 600,
                          borderTop: "1px solid #ecf0f1",
                          paddingTop: 10,
                          display: "flex",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 10,
                        }}
                      >
                        <div>
                          <strong>Equipment:</strong> {rate?.equipment_type ?? "N/A"}
                        </div>
                        <div>
                          <strong>Confidence:</strong> {rate?.confidence_score ?? "N/A"}
                        </div>
                        <div>
                          <strong>Loads:</strong> {rate?.total_load_count ?? "N/A"}
                        </div>
                      </div>

                      {formatUpdatedLine(rate, rateMeta) && (
                        <div
                          style={{
                            marginTop: 8,
                            fontSize: 11,
                            color: "#95a5a6",
                            textAlign: "right",
                            fontStyle: "italic",
                          }}
                        >
                          {formatUpdatedLine(rate, rateMeta)}
                        </div>
                      )}
                    </div>

                  ))}
                </div>
              ) : !loading && rateData?.length === 0 ? (
                <div style={{ marginTop: 20, fontSize: 14, color: "#555" }}>
                  No rate data found.
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
